import { memo } from 'react'
import { View, Text } from 'react-native'
import { LinearGradient } from 'expo-linear-gradient'
import { useTranslation } from 'react-i18next'
import { Clock, MapPin, Star } from '@/components/ui/icons'
import { Image } from '@/components/ui/Image'
import { Pressable } from '@/components/ui/Pressable'
import { CountryFlag } from '@/components/ui/CountryFlag'
import { useTheme } from '@/lib/hooks/use-theme'
import { usePressAction } from '@/lib/hooks/use-press-action'
import { prefetchTourExperience } from '@/lib/hooks/use-tours'
import { useSemanticColors } from '@/lib/theme/tokens'
import { COLORS, FONTS, SPACING, RADIUS } from '@/lib/brand'
import { findCountry } from '@/lib/data/countries'
import { catalogCategoryLabel, formatTourDuration } from '@/lib/tours/catalog-layout'
import { GENERIC_IMAGE_BLURHASH } from '@/lib/utils/blurhash'
import type { TourCatalogExperience } from '@/lib/types/tours.types'

export type TourCatalogCardLayout = 'compact' | 'tile'

interface TourCatalogCardProps {
  experience: TourCatalogExperience
  onPress: () => void
  layout?: TourCatalogCardLayout
}

function CoverFallback({ colors }: { colors: ReturnType<typeof useSemanticColors> }) {
  return (
    <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
      <MapPin size={22} color={colors.textMuted} strokeWidth={1.5} />
    </View>
  )
}

function CategoryPill({ label }: { label: string }) {
  return (
    <View
      style={{
        backgroundColor: 'rgba(255,255,255,0.92)',
        paddingHorizontal: SPACING.sm + 1,
        paddingVertical: SPACING.xs,
        borderRadius: RADIUS.sm,
      }}
    >
      <Text style={{ fontSize: 12, fontFamily: FONTS.body, color: COLORS.purple.DEFAULT }}>
        {label}
      </Text>
    </View>
  )
}

function DestinationChip({
  iso2,
  name,
  light,
  colors,
  testID,
}: {
  iso2: string
  name: string
  light?: boolean
  colors: ReturnType<typeof useSemanticColors>
  testID: string
}) {
  return (
    <View
      style={{
        alignSelf: 'flex-start',
        flexDirection: 'row',
        alignItems: 'center',
        gap: SPACING.xs,
        paddingHorizontal: SPACING.sm,
        paddingVertical: SPACING.xs,
        borderRadius: RADIUS.full,
        backgroundColor: light ? 'rgba(255,255,255,0.18)' : colors.scrim,
      }}
    >
      <CountryFlag iso2={iso2} width={16} />
      <Text style={{ fontFamily: FONTS.body, fontSize: 12, color: COLORS.white }} testID={testID}>
        {name}
      </Text>
    </View>
  )
}

function MetaRow({
  durationLabel,
  distanceKm,
  ratingAvg,
  ratingCount,
  colors,
  experienceId,
  onPhoto,
}: {
  durationLabel: string
  distanceKm?: number | null
  ratingAvg?: number
  ratingCount?: number
  colors: ReturnType<typeof useSemanticColors>
  experienceId: string
  onPhoto?: boolean
}) {
  const ink = onPhoto ? COLORS.white : colors.textMuted
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap' }}>
      <Clock size={13} color={ink} strokeWidth={1.8} />
      <Text
        style={{
          marginLeft: SPACING.xs,
          fontFamily: FONTS.body,
          fontSize: 12,
          color: ink,
        }}
      >
        {durationLabel}
        {distanceKm ? ` · ${distanceKm} km` : ''}
      </Text>
      {ratingCount && ratingCount > 0 ? (
        <>
          <Star
            size={13}
            color={onPhoto ? COLORS.white : colors.warning}
            fill={onPhoto ? COLORS.white : colors.warning}
            strokeWidth={1.6}
            style={{ marginLeft: SPACING.sm }}
          />
          <Text
            style={{
              marginLeft: SPACING.xs,
              fontFamily: FONTS.body,
              fontSize: 12,
              color: ink,
            }}
            testID={`tours.catalog-rating-${experienceId}`}
          >
            {ratingAvg?.toFixed(1)} ({ratingCount})
          </Text>
        </>
      ) : null}
    </View>
  )
}

export const TourCatalogCard = memo(function TourCatalogCard({
  experience,
  onPress,
  layout = 'compact',
}: TourCatalogCardProps) {
  // Cover + title + route + duration. Never a "desde $ por persona" row —
  // that was catalog bait and it ate a full line on every card.
  const { t, i18n } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const handlePress = usePressAction(() => {
    prefetchTourExperience(experience.id)
    onPress()
  })

  const durationLabel = formatTourDuration(experience.durationMinutes)
  const destinationCountry = experience.destinationCountryCode
    ? findCountry(experience.destinationCountryCode)
    : undefined
  const destinationName = destinationCountry
    ? i18n.language.startsWith('en')
      ? destinationCountry.nameEn
      : destinationCountry.nameEs
    : null
  const categoryName = catalogCategoryLabel(experience, i18n.language.startsWith('en'))
  const isTile = layout === 'tile'
  const coverAspect = isTile ? 3 / 2 : undefined
  const coverHeight = layout === 'compact' ? 140 : undefined
  const coverTone = isDark ? 'rgba(255,255,255,0.05)' : colors.surfaceSubtle

  return (
    <Pressable
      onPress={handlePress}
      onHoverIn={() => prefetchTourExperience(experience.id)}
      accessibilityRole="button"
      accessibilityLabel={experience.title}
      testID={`tours.catalog-card-${experience.id}`}
    >
      <View
        style={{
          height: coverHeight,
          aspectRatio: coverAspect,
          borderRadius: RADIUS.lg,
          overflow: 'hidden',
          position: 'relative',
          backgroundColor: coverTone,
        }}
      >
        {experience.coverImageUrl ? (
          <Image
            source={experience.coverImageUrl}
            // El grid de tours vive en FlashList: sin clave, la celda reciclada
            // puede mostrar la portada del tour anterior por un frame.
            recyclingKey={experience.id}
            blurhash={GENERIC_IMAGE_BLURHASH}
            width="100%"
            height="100%"
            contentFit="cover"
            accessibilityLabel={t('tours.cover_alt', { title: experience.title })}
          />
        ) : (
          <CoverFallback colors={colors} />
        )}
        {categoryName ? (
          <View
            style={{
              position: 'absolute',
              top: SPACING.sm + 2,
              left: SPACING.sm + 2,
            }}
          >
            <CategoryPill label={categoryName} />
          </View>
        ) : null}
        {isTile ? (
          <LinearGradient
            colors={['rgba(0,0,0,0)', 'rgba(0,0,0,0.28)', 'rgba(0,0,0,0.78)'] as const}
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              paddingHorizontal: SPACING.md,
              paddingTop: SPACING.xl,
              paddingBottom: SPACING.md,
              gap: SPACING.xs,
            }}
          >
            {destinationName && experience.destinationCountryCode ? (
              <DestinationChip
                iso2={experience.destinationCountryCode}
                name={destinationName}
                light
                colors={colors}
                testID={`tours.catalog-destination-${experience.id}`}
              />
            ) : null}
            <Text
              numberOfLines={2}
              style={{
                fontFamily: FONTS.bodyBold,
                fontSize: 17,
                lineHeight: 22,
                color: COLORS.white,
                letterSpacing: -0.3,
              }}
            >
              {experience.title}
            </Text>
            <Text
              numberOfLines={1}
              style={{
                fontFamily: FONTS.body,
                fontSize: 13,
                color: COLORS.white,
              }}
            >
              {t('tours.route', {
                origin: experience.originLabel,
                destination: experience.destinationLabel,
              })}
            </Text>
            <MetaRow
              durationLabel={durationLabel}
              distanceKm={experience.distanceKm}
              ratingAvg={experience.ratingAvg}
              ratingCount={experience.ratingCount}
              colors={colors}
              experienceId={experience.id}
              onPhoto
            />
          </LinearGradient>
        ) : destinationName && experience.destinationCountryCode ? (
          <View style={{ position: 'absolute', left: SPACING.sm, bottom: SPACING.sm }}>
            <DestinationChip
              iso2={experience.destinationCountryCode}
              name={destinationName}
              colors={colors}
              testID={`tours.catalog-destination-${experience.id}`}
            />
          </View>
        ) : null}
      </View>

      {isTile ? null : (
        <View style={{ paddingTop: SPACING.sm + 2, paddingHorizontal: 2 }}>
          <Text
            numberOfLines={2}
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 17,
              lineHeight: 22,
              color: colors.textPrimary,
              letterSpacing: -0.3,
            }}
          >
            {experience.title}
          </Text>
          <Text
            numberOfLines={1}
            style={{
              marginTop: 2,
              fontFamily: FONTS.body,
              fontSize: 13,
              color: colors.textSecondary,
            }}
          >
            {t('tours.route', {
              origin: experience.originLabel,
              destination: experience.destinationLabel,
            })}
          </Text>
          <View style={{ marginTop: SPACING.xs }}>
            <MetaRow
              durationLabel={durationLabel}
              distanceKm={experience.distanceKm}
              ratingAvg={experience.ratingAvg}
              ratingCount={experience.ratingCount}
              colors={colors}
              experienceId={experience.id}
            />
          </View>
        </View>
      )}
    </Pressable>
  )
})
